<script setup>
import { ref, computed, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import unhappyCasesAPI from 'dashboard/api/unhappyCases';
import BaseSettingsHeader from '../settings/components/BaseSettingsHeader.vue';
import {
  STATUS_CLASSES,
  SIGNAL_CLASSES,
  useUnhappyCaseConstants,
} from 'dashboard/composables/useUnhappyCaseConstants';

const { t } = useI18n();
const router = useRouter();
const { accountId } = useAccount();
const { SIGNAL_LABELS } = useUnhappyCaseConstants();

const cases = ref([]);
const totalCount = ref(0);
const currentPage = ref(1);
const isLoading = ref(false);
const selectedIds = ref([]);
const isBulkUpdating = ref(false);
const pendingBulkStatus = ref(null);

const statusFilter = ref('');
const trustedOnly = ref(false);

const STATUS_FILTERS = [
  { value: '', label: t('UNHAPPY_CASES.LIST.FILTERS.ALL') },
  { value: 'pending', label: t('UNHAPPY_CASES.LIST.FILTERS.PENDING') },
  { value: 'confirmed', label: t('UNHAPPY_CASES.LIST.FILTERS.CONFIRMED') },
  { value: 'rejected', label: t('UNHAPPY_CASES.LIST.FILTERS.REJECTED') },
];

const allSelected = computed(
  () =>
    cases.value.length > 0 && selectedIds.value.length === cases.value.length
);

const totalPages = computed(() => Math.ceil(totalCount.value / 25));

const fetchCases = async () => {
  isLoading.value = true;
  selectedIds.value = [];
  pendingBulkStatus.value = null;
  try {
    const params = {
      page: currentPage.value,
      ...(statusFilter.value && { review_status: statusFilter.value }),
      ...(trustedOnly.value && { trusted_only: true }),
    };
    const { data } = await unhappyCasesAPI.get(params);
    cases.value = data.data.payload;
    totalCount.value = data.data.meta.total_count;
  } catch {
    cases.value = [];
  } finally {
    isLoading.value = false;
  }
};

const setStatusFilter = value => {
  statusFilter.value = value;
  trustedOnly.value = false;
  currentPage.value = 1;
  fetchCases();
};

const setTrustedFilter = () => {
  trustedOnly.value = !trustedOnly.value;
  statusFilter.value = '';
  currentPage.value = 1;
  fetchCases();
};

const goToPage = page => {
  if (page < 1 || page > totalPages.value) return;
  currentPage.value = page;
  fetchCases();
};

const toggleSelect = id => {
  const idx = selectedIds.value.indexOf(id);
  if (idx === -1) selectedIds.value.push(id);
  else selectedIds.value.splice(idx, 1);
};

const toggleSelectAll = () => {
  if (allSelected.value) selectedIds.value = [];
  else selectedIds.value = cases.value.map(c => c.id);
};

const requestBulkStatus = status => {
  pendingBulkStatus.value = status;
};

const cancelBulkStatus = () => {
  pendingBulkStatus.value = null;
};

const confirmBulkStatus = async () => {
  const status = pendingBulkStatus.value;
  pendingBulkStatus.value = null;
  isBulkUpdating.value = true;
  try {
    await unhappyCasesAPI.bulkUpdate(selectedIds.value, status);
    selectedIds.value = [];
    fetchCases();
  } catch {
    useAlert(t('UNHAPPY_CASES.SETTINGS.SAVE_ERROR'));
  } finally {
    isBulkUpdating.value = false;
  }
};

const STATUS_LABELS = {
  pending: t('UNHAPPY_CASES.LIST.FILTERS.PENDING'),
  confirmed: t('UNHAPPY_CASES.LIST.FILTERS.CONFIRMED'),
  rejected: t('UNHAPPY_CASES.LIST.FILTERS.REJECTED'),
};

const goToDetail = id => {
  router.push({
    name: 'unhappy_cases_show',
    params: { accountId: accountId.value, id },
  });
};

const formatDate = ts => {
  if (!ts) return '-';
  return new Date(ts * 1000).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

onBeforeMount(fetchCases);
</script>

<template>
  <div
    class="flex flex-col w-full h-full overflow-auto bg-n-background font-inter"
  >
    <div class="w-full max-w-5xl mx-auto px-6 py-8 flex flex-col gap-6">
      <!-- Header -->
      <BaseSettingsHeader
        :title="$t('UNHAPPY_CASES.LIST.TITLE')"
        :description="$t('UNHAPPY_CASES.LIST.DESCRIPTION')"
      />

      <!-- Filter pills + count -->
      <div class="flex items-center gap-2 flex-wrap">
        <div
          class="flex items-center gap-1 p-1 rounded-xl bg-n-alpha-1 border border-n-weak"
        >
          <button
            v-for="opt in STATUS_FILTERS"
            :key="opt.value"
            type="button"
            class="px-3 py-1 text-sm rounded-lg transition-colors"
            :class="
              statusFilter === opt.value && !trustedOnly
                ? 'bg-n-solid-3 text-n-slate-12 shadow-sm font-medium'
                : 'text-n-slate-10 hover:text-n-slate-12'
            "
            @click="setStatusFilter(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>

        <button
          type="button"
          class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors"
          :class="
            trustedOnly
              ? 'border-n-iris-5 bg-n-iris-3 text-n-iris-11 font-medium'
              : 'border-n-weak text-n-slate-10 hover:text-n-slate-12'
          "
          @click="setTrustedFilter"
        >
          <fluent-icon icon="star-emphasis" size="12" />
          {{ $t('UNHAPPY_CASES.LIST.FILTERS.TRUSTED_ONLY') }}
        </button>

        <span class="ml-auto text-sm text-n-slate-9">
          {{ totalCount }}
          {{ $t('UNHAPPY_CASES.LIST.FILTERS.ALL').toLowerCase() }}
        </span>
      </div>

      <!-- Bulk action bar -->
      <Transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0 -translate-y-1"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition-all duration-150 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-1"
      >
        <div
          v-if="selectedIds.length"
          class="flex items-center gap-2 px-3 py-2 rounded-xl border border-n-slate-5 bg-n-solid-3 shadow-md min-h-[44px]"
        >
          <!-- Phase 1: action selection -->
          <template v-if="!pendingBulkStatus">
            <span
              class="flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full bg-woot-500 text-white text-xs font-semibold tabular-nums"
            >
              {{ selectedIds.length }}
            </span>
            <span class="text-sm text-n-slate-11">
              {{ $t('UNHAPPY_CASES.LIST.BULK.SELECTED') }}
            </span>

            <div class="w-px h-5 bg-n-weak mx-1" />

            <button
              type="button"
              class="px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors border-n-amber-5 bg-n-amber-3 text-n-amber-11 hover:bg-n-amber-4"
              @click="requestBulkStatus('pending')"
            >
              {{ $t('UNHAPPY_CASES.LIST.BULK.MARK_PENDING') }}
            </button>
            <button
              type="button"
              class="px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors border-n-teal-5 bg-n-teal-3 text-n-teal-11 hover:bg-n-teal-4"
              @click="requestBulkStatus('confirmed')"
            >
              {{ $t('UNHAPPY_CASES.LIST.BULK.MARK_CONFIRMED') }}
            </button>
            <button
              type="button"
              class="px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors border-n-ruby-5 bg-n-ruby-3 text-n-ruby-11 hover:bg-n-ruby-4"
              @click="requestBulkStatus('rejected')"
            >
              {{ $t('UNHAPPY_CASES.LIST.BULK.MARK_REJECTED') }}
            </button>

            <button
              type="button"
              class="ml-auto flex items-center justify-center w-7 h-7 rounded-lg text-n-slate-9 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              :title="$t('UNHAPPY_CASES.LIST.BULK.DESELECT')"
              @click="selectedIds = []"
            >
              <fluent-icon icon="dismiss" size="14" />
            </button>
          </template>

          <!-- Phase 2: confirmation -->
          <template v-else>
            <span class="text-sm text-n-slate-12 flex-1">
              {{
                $t('UNHAPPY_CASES.LIST.BULK.CONFIRM_PROMPT', {
                  count: selectedIds.length,
                  status: STATUS_LABELS[pendingBulkStatus],
                })
              }}
            </span>
            <button
              type="button"
              class="px-3 py-1.5 text-xs font-semibold rounded-lg bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-50 transition-colors"
              :disabled="isBulkUpdating"
              @click="confirmBulkStatus"
            >
              {{ $t('UNHAPPY_CASES.LIST.BULK.CONFIRM') }}
            </button>
            <button
              type="button"
              class="px-3 py-1.5 text-xs font-medium rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
              :disabled="isBulkUpdating"
              @click="cancelBulkStatus"
            >
              {{ $t('UNHAPPY_CASES.LIST.BULK.CANCEL') }}
            </button>
          </template>
        </div>
      </Transition>

      <!-- Loading -->
      <div v-if="isLoading" class="py-20 text-center text-sm text-n-slate-9">
        {{ $t('UNHAPPY_CASES.LIST.LOADING') }}
      </div>

      <!-- Empty state -->
      <div
        v-else-if="!cases.length"
        class="py-20 flex flex-col items-center gap-3 text-center"
      >
        <div
          class="w-12 h-12 rounded-full bg-n-alpha-2 flex items-center justify-center"
        >
          <fluent-icon icon="warning" size="24" class="text-n-slate-9" />
        </div>
        <p class="text-sm text-n-slate-9">
          {{ $t('UNHAPPY_CASES.LIST.EMPTY') }}
        </p>
      </div>

      <!-- List -->
      <div v-else class="flex flex-col gap-2">
        <!-- Select all row -->
        <label
          class="flex items-center gap-2 px-1 text-xs text-n-slate-9 cursor-pointer select-none"
        >
          <input
            type="checkbox"
            class="rounded accent-woot-500"
            :checked="allSelected"
            @change="toggleSelectAll"
          />
          {{
            allSelected
              ? $t('UNHAPPY_CASES.LIST.BULK.CLEAR')
              : $t('UNHAPPY_CASES.LIST.FILTERS.ALL')
          }}
        </label>

        <!-- Case rows -->
        <div
          v-for="item in cases"
          :key="item.id"
          class="group flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-all cursor-pointer"
          :class="
            selectedIds.includes(item.id)
              ? 'border-woot-300 bg-woot-25 dark:border-woot-700 dark:bg-woot-900/10'
              : 'border-n-weak bg-n-solid-2 hover:border-n-slate-5 hover:bg-n-solid-3'
          "
          @click="goToDetail(item.id)"
        >
          <!-- Checkbox -->
          <label class="shrink-0 cursor-pointer" @click.stop>
            <input
              type="checkbox"
              class="rounded accent-woot-500"
              :checked="selectedIds.includes(item.id)"
              @change.stop="toggleSelect(item.id)"
            />
          </label>

          <!-- Conversation ID + contact -->
          <div class="w-40 shrink-0 flex flex-col gap-0.5 min-w-0">
            <div class="flex items-center gap-1">
              <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
              <span class="text-sm font-semibold text-n-slate-12">
                #{{ item.conversation?.display_id || item.conversation_id }}
              </span>
              <fluent-icon
                v-if="item.is_trusted"
                icon="star-emphasis"
                size="12"
                class="shrink-0 text-n-iris-9"
              />
            </div>
            <span
              v-if="item.conversation?.contact_name"
              class="text-xs text-n-slate-9 truncate"
            >
              {{ item.conversation.contact_name }}
            </span>
          </div>

          <!-- Signals -->
          <div class="flex flex-wrap gap-1.5 flex-1 min-w-0">
            <span
              v-for="signal in item.signals"
              :key="signal.type"
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
              :class="
                SIGNAL_CLASSES[signal.type] ||
                'bg-n-alpha-2 text-n-slate-11 border-n-weak'
              "
            >
              {{ SIGNAL_LABELS[signal.type] || signal.type }}
              <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
              <span v-if="signal.value" class="opacity-70 font-normal">
                · {{ signal.value }}
              </span>
            </span>
          </div>

          <!-- AI items count -->
          <span
            v-if="item.ai_items?.length"
            class="shrink-0 text-xs text-n-slate-9"
          >
            {{
              $t('UNHAPPY_CASES.LIST.AI_ITEMS', { count: item.ai_items.length })
            }}
          </span>

          <!-- Status badge -->
          <span
            class="shrink-0 px-2.5 py-0.5 rounded-full text-xs font-medium border"
            :class="STATUS_CLASSES[item.review_status]"
          >
            {{ item.review_status }}
          </span>

          <!-- Date -->
          <span class="shrink-0 text-xs text-n-slate-9 w-24 text-right">
            {{ formatDate(item.created_at) }}
          </span>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="flex items-center justify-between pt-2">
        <button
          type="button"
          class="flex items-center gap-1 text-sm text-n-slate-10 hover:text-n-slate-12 disabled:opacity-40 transition-colors"
          :disabled="currentPage === 1"
          @click="goToPage(currentPage - 1)"
        >
          <fluent-icon icon="chevron-left" size="14" />
          {{ $t('UNHAPPY_CASES.LIST.PAGINATION.PREV') }}
        </button>
        <span class="text-sm text-n-slate-9">
          {{
            $t('UNHAPPY_CASES.LIST.PAGINATION.OF', {
              current: currentPage,
              total: totalPages,
            })
          }}
        </span>
        <button
          type="button"
          class="flex items-center gap-1 text-sm text-n-slate-10 hover:text-n-slate-12 disabled:opacity-40 transition-colors"
          :disabled="currentPage === totalPages"
          @click="goToPage(currentPage + 1)"
        >
          {{ $t('UNHAPPY_CASES.LIST.PAGINATION.NEXT') }}
          <fluent-icon icon="chevron-right" size="14" />
        </button>
      </div>
    </div>
  </div>
</template>
